/**
 * Serviço de Autenticação
 * Comunicação com o backend NestJS para login, registro e gerenciamento de tokens
 */

import { env } from '../config/env';
import i18n from '../config/i18n';
import { isTokenExpired, isValidJWTFormat } from '../utils/jwtUtils';
import { formatEndDate } from '../utils/accessDate';
import { esquecerAvisoContexto } from '../utils/preceptorContexto';

const TOKEN_KEY = 'radexpert_token';
const USER_KEY = 'radexpert_user';
const SESSION_EXPIRED_EVENT = 'auth:session-expired';

function notifySessionExpired(): void {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

export { SESSION_EXPIRED_EVENT };

/**
 * Acesso negado pelo backend no meio da sessão (conta bloqueada ou prazo
 * vencido). Diferente da sessão expirada: aqui a tela de login precisa dizer o
 * motivo (TEXTO 6).
 */
export const ACCESS_DENIED_EVENT = 'auth:access-denied';

export type AccessDeniedCode = 'ACCESS_EXPIRED' | 'ACCOUNT_BLOCKED';

export interface AccessDeniedDetail {
  code: AccessDeniedCode;
  /** Último dia de acesso ('YYYY-MM-DD'), no vencimento. */
  accessEndDate: string | null;
}

/** Texto para o aluno, no idioma dele. A `message` do backend vem sempre em português. */
export function accessDeniedMessage(detail: AccessDeniedDetail): string {
  if (detail.code === 'ACCOUNT_BLOCKED') return i18n.t('access.blocked');
  return detail.accessEndDate
    ? i18n.t('access.expiredOn', { date: formatEndDate(detail.accessEndDate) })
    : i18n.t('access.expired');
}

export class AccessDeniedError extends Error {
  constructor(readonly detail: AccessDeniedDetail) {
    super(accessDeniedMessage(detail));
    this.name = 'AccessDeniedError';
  }
}

/** O corpo é um 403 de acesso negado? Os demais 403 (admin, Preceptor) não são. */
export function readAccessDenied(body: unknown): AccessDeniedDetail | null {
  if (!body || typeof body !== 'object') return null;
  const { code, accessEndDate } = body as { code?: unknown; accessEndDate?: unknown };
  if (code !== 'ACCESS_EXPIRED' && code !== 'ACCOUNT_BLOCKED') return null;
  return {
    code,
    accessEndDate: typeof accessEndDate === 'string' ? accessEndDate : null,
  };
}


export interface User {
  id: string;
  email: string;
  name: string;
  productId: number;
  paymentStatus: string;
  role: string;
  isBlocked: boolean;
  preceptorEnabled: boolean;
}

/**
 * Verifica se o usuário tem acesso ao Preceptor AI.
 * Flag gerenciada por usuário no backend; admins têm bypass.
 */
export function canAccessPreceptor(user?: User | null): boolean {
  if (!user) return false;
  return user.preceptorEnabled === true || user.role === 'admin';
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  try {
    const response = await fetch(`${env.apiBaseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const denied = readAccessDenied(error);
      if (denied) throw new AccessDeniedError(denied);
      throw new Error(error.message || i18n.t('auth.loginError'));
    }

    const data: AuthResponse = await response.json();
    
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // A Política (cl. 2.8) promete que o que o app guarda no navegador sai no
  // logout — e a dispensa do aviso de uma conta não deve valer para a próxima.
  esquecerAvisoContexto();
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): User | null {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  const token = getStoredToken();
  
  if (!token) {
    return false;
  }
  
  if (!isValidJWTFormat(token)) {
    console.warn('Token armazenado tem formato inválido');
    logout();
    return false;
  }
  
  if (isTokenExpired(token)) {
    console.log('Token expirado, fazendo logout automático');
    logout();
    return false;
  }
  
  return true;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  const response = await authenticatedFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || i18n.t('auth.changePasswordError'));
  }

  return response.json();
}

/**
 * Respostas que encerram a sessão, nos três wrappers (JSON, formulário, blob):
 * 401 (token inválido) e 403 de acesso negado (conta bloqueada, prazo vencido),
 * que o backend confere a cada requisição. Os outros 403 (admin, Preceptor)
 * seguem para quem chamou; `clone()` porque esse alguém ainda lê o corpo.
 */
async function handleAuthFailure(response: Response): Promise<void> {
  if (response.status === 401) {
    console.log('Erro 401: token inválido ou expirado, fazendo logout');
    logout();
    notifySessionExpired();
    throw new Error(i18n.t('auth.sessionInvalid'));
  }

  if (response.status === 403) {
    const denied = readAccessDenied(await response.clone().json().catch(() => null));
    if (denied) {
      logout();
      window.dispatchEvent(new CustomEvent<AccessDeniedDetail>(ACCESS_DENIED_EVENT, { detail: denied }));
      throw new AccessDeniedError(denied);
    }
  }
}

export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getStoredToken();
  
  if (!token) {
    throw new Error(i18n.t('auth.userNotAuthenticated'));
  }
  
  if (isTokenExpired(token)) {
    console.log('Token expirado ao tentar fazer requisição, fazendo logout');
    logout();
    notifySessionExpired();
    throw new Error(i18n.t('auth.sessionExpired'));
  }
  
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-Requested-With': 'XMLHttpRequest',
    ...options.headers,
  };
  
  try {
    const response = await fetch(`${env.apiBaseUrl}${endpoint}`, {
      ...options,
      headers,
    });
    
    await handleAuthFailure(response);

    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Envio multipart (upload de arquivos). Igual a authenticatedFetch, mas sem
 * Content-Type: o navegador define o boundary do FormData.
 */
export async function authenticatedFetchForm(
  endpoint: string,
  body: FormData,
  method: 'POST' | 'PATCH' = 'POST',
): Promise<Response> {
  const token = getStoredToken();

  if (!token) {
    throw new Error(i18n.t('auth.userNotAuthenticated'));
  }

  if (isTokenExpired(token)) {
    logout();
    notifySessionExpired();
    throw new Error(i18n.t('auth.sessionExpired'));
  }

  const response = await fetch(`${env.apiBaseUrl}${endpoint}`, {
    method,
    body,
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  await handleAuthFailure(response);

  return response;
}

export async function authenticatedFetchBlob(
  endpoint: string,
  options: RequestInit = {}
): Promise<Blob> {
  const token = getStoredToken();

  if (!token) {
    throw new Error(i18n.t('auth.userNotAuthenticated'));
  }

  if (isTokenExpired(token)) {
    logout();
    notifySessionExpired();
    throw new Error(i18n.t('auth.sessionExpired'));
  }

  const headers = {
    'Authorization': `Bearer ${token}`,
    'X-Requested-With': 'XMLHttpRequest',
    ...options.headers,
  };

  const response = await fetch(`${env.apiBaseUrl}${endpoint}`, {
    ...options,
    headers,
  });

  await handleAuthFailure(response);

  if (!response.ok) {
    throw new Error(i18n.t('auth.requestFailed'));
  }

  return response.blob();
}
